import { Persona } from "@/api/personas";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
} from "@/components/ui/table";
import { Link } from "react-router-dom";
import { PersonaAvatar } from "@/components/PersonaAvatar";
import { cn, formatLargeNumber } from "@/lib/utils";
import {
  TrendingUp,
  TrendingDown,
  ArrowDown,
  ArrowUp,
  Crown,
  Copy,
} from "lucide-react";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { Fragment, useMemo, useState } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { convertISODateToRelativeTime } from "@/lib/date";
import { Button } from "../ui/button";
import { useToast } from "@/hooks/use-toast";
import { X } from "@/components/logos/X";
import { Solscan } from "@/components/logos/Solscan";
import { clsx } from "clsx";

export function ManagePersonasTable({
  className,
  loading,
  personas,
  tokenMetrics,
}: {
  className?: string;
  loading: boolean;
  personas: Persona[];
  tokenMetrics: Map<string, any>;
}) {
  const [sorting, setSorting] = useState<SortingState>([
    {
      id: "marketCap",
      desc: true,
    },
  ]);

  const columns = useMemo<ColumnDef<Persona>[]>(
    () =>
      [
        {
          id: "publicAgent",
          accessorFn: (persona) => (persona.public_persona ? "yes" : "no"),
          header: "Public",
          cell: (info) => info.getValue(),
          sortingFn: "basic",
          sortUndefined: "last",
        },
        {
          id: "name",
          accessorKey: "name",
          header: "Token",
          cell: ({ cell }) => (
            <AgentCell key={cell.id} persona={cell.row.original} />
          ),
          sortingFn: "text",
        },
        {
          id: "liquidity",
          accessorFn: (persona) => {
            const personaTokenMetrics = tokenMetrics.get(persona.persona_id);
            return personaTokenMetrics?.liquidity;
          },
          header: "Liquidity",
          cell: ({ getValue }) => {
            const value = getValue() as number | null | undefined;

            return (
              <TableCell
                className={clsx(
                  "align-middle p-3 text-xl font-semibold text-muted-foreground",
                  value === undefined ? "text-center text-lg" : "",
                )}
                colSpan={value === undefined ? 5 : 1}
              >
                {value !== undefined ? formatLargeNumber(value) : "Coming Soon"}
              </TableCell>
            );
          },
          sortingFn: "auto",
          sortUndefined: "last",
        },
        {
          id: "marketCap",
          accessorFn: (persona) => {
            const personaTokenMetrics = tokenMetrics.get(persona.persona_id);
            return personaTokenMetrics?.marketCap;
          },
          header: "Market Cap",
          cell: ({ getValue }) => {
            const value = getValue() as number | null | undefined;
            return value ? (
              <TableCell className="align-middle p-3 text-xl font-semibold text-muted-foreground">
                {formatLargeNumber(value)}
              </TableCell>
            ) : undefined;
          },
          sortingFn: "auto",
          sortUndefined: "last",
        },
        {
          id: "24hChg",
          accessorFn: (persona) => {
            const personaTokenMetrics = tokenMetrics.get(persona.persona_id);
            const value = parseFloat(personaTokenMetrics?.priceChange24h);
            return Number.isFinite(value) ? value : undefined;
          },
          header: "24h Change",
          cell: ({ cell }) => {
            const priceChange24h = cell.getValue<number>();
            return priceChange24h != null ? (
              <TableCell
                key={cell.id}
                className="align-middle p-3 font-bold text-muted-foreground"
              >
                {priceChange24h != null ? (
                  <div className="flex items-center gap-1">
                    {priceChange24h > 0 ? (
                      <TrendingUp className="w-4 h-4 text-green-500" />
                    ) : priceChange24h < 0 ? (
                      <TrendingDown className="w-4 h-4 text-red-500" />
                    ) : null}
                    <span
                      className={
                        priceChange24h > 0
                          ? "text-green-500"
                          : priceChange24h < 0
                            ? "text-red-500"
                            : ""
                      }
                    >
                      {priceChange24h}%
                    </span>
                  </div>
                ) : null}
              </TableCell>
            ) : null;
          },
          sortingFn: "basic",
          sortUndefined: "last",
        },
        {
          id: "holders",
          accessorFn: (persona) => {
            const personaTokenMetrics = tokenMetrics.get(persona.persona_id);
            return personaTokenMetrics?.holders;
          },
          header: "Holders",
          cell: ({ getValue }) => {
            const value = getValue() as number | null | undefined;
            return value ? (
              <TableCell className="align-middle p-3 text-xl font-semibold text-muted-foreground">
                {value}
              </TableCell>
            ) : undefined;
          },
          sortingFn: "auto",
          sortUndefined: "last",
        },
        {
          id: "24hVolume",
          accessorFn: (persona) => {
            const personaTokenMetrics = tokenMetrics.get(persona.persona_id);
            return personaTokenMetrics?.volume24h;
          },
          header: "24h Volume",
          cell: ({ getValue }) => {
            const value = getValue() as number | null | undefined;

            return (
              <TableCell className="align-middle p-3 text-xl font-semibold text-muted-foreground">
                {value ? formatLargeNumber(value) : ""}
              </TableCell>
            );
          },
          sortingFn: "basic",
          sortUndefined: "last",
        },
        {
          id: "buy",
          accessorFn: (persona) => {
            const personaTokenMetrics = tokenMetrics.get(persona.persona_id);
            return personaTokenMetrics?.liquidity !== undefined
              ? persona.web3?.metadata?.symbol
              : undefined;
          },
          header: "Buy",
          cell: ({ getValue }) => (
            <TableCell className="align-middle p-3 text-xl font-semibold text-muted-foreground">
              {getValue() ? (
                <Button
                  variant="outline"
                  className="px-5 py-5 text-lg normal-case text-accent"
                >
                  Buy
                </Button>
              ) : (
                ""
              )}
            </TableCell>
          ),
          enableSorting: false,
        },
      ] satisfies ColumnDef<Persona>[],
    [tokenMetrics],
  );

  const table = useReactTable({
    columns,
    data: loading ? Array(5).fill({}) : personas,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
      columnVisibility: {
        publicAgent: false,
      },
    },
  });

  return (
    <Table
      className={cn("bg-darkGray", loading ? "min-w-[1280px]" : "", className)}
    >
      <TableHeader>
        <TableRow
          className="*:px-2 hover:bg-transparent border-gray-700"
          data-testid="personas-table-information"
        >
          {table.getHeaderGroups().map((headerGroup) =>
            headerGroup.headers.map((header) => (
              <TableHead
                key={header.id}
                className="min-w-32 hover:bg-gray-800 cursor-pointer"
                onClick={() => header.column.toggleSorting()}
              >
                <div className="flex flex-row gap-x-1 items-center">
                  <div className="flex-shrink-0">
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                  </div>
                  {header.column.getIsSorted() === "asc" ? (
                    <ArrowUp className="h-4" />
                  ) : header.column.getIsSorted() === "desc" ? (
                    <ArrowDown className="h-4" />
                  ) : null}
                </div>
              </TableHead>
            )),
          )}
        </TableRow>
      </TableHeader>
      <TableBody
        className={cn("text-lg font-semibold", loading ? "animate-pulse" : "")}
      >
        {table.getRowModel().rows.map((row) => {
          const persona = row.original;
          const hasToken = persona?.web3?.metadata?.symbol;

          return (
            <Link
              key={row.id}
              className="table-row [&:not(:last-child)]:border-b border-gray-700 transition-colors data-[state=selected]:bg-muted *:px-2 hover:bg-gray-800"
              to={persona.persona_id ? `/agents/${persona.persona_id}` : "#"}
              role="row"
              data-testid={`agent-id-${persona.persona_id}`}
              data-agents-page-tour={
                row.index === 0 ? "first-persona-row" : undefined
              }
            >
              {hasToken ? (
                row
                  .getVisibleCells()
                  .map((cell) => (
                    <Fragment key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </Fragment>
                  ))
              ) : (
                <>
                  <TableCell>
                    <div className="flex flex-row items-center gap-x-4 min-w-96">
                      <PersonaAvatar className="h-[52px]" persona={persona} />
                      <div className="flex flex-col gap-0 text-xl">
                        <div className="flex flex-row gap-2">
                          {persona.public_persona && (
                            <Tooltip>
                              <TooltipTrigger>
                                <Crown className="w-4 h-4 text-accent" />
                              </TooltipTrigger>
                              <TooltipContent>Public Agent</TooltipContent>
                            </Tooltip>
                          )}
                          <span className="max-w-52 truncate">
                            {persona.name}
                          </span>
                        </div>
                        {persona.created_at && (
                          <div className="text-[#6fff1b] text-lg">
                            {convertISODateToRelativeTime(persona.created_at)}
                          </div>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell
                    colSpan={5}
                    className="text-center text-muted-foreground ml-4"
                  >
                    Token Coming Soon
                  </TableCell>
                </>
              )}
            </Link>
          );
        })}
      </TableBody>
    </Table>
  );
}

function AgentCell({ persona }: { persona: Persona }) {
  const { toast } = useToast();

  return (
    <TableCell className="text-lg font-semibold">
      <div className="flex flex-row items-center gap-x-4 min-w-96">
        <PersonaAvatar className="h-[52px]" persona={persona} />
        <div className="flex flex-col gap-0 text-xl">
          <div className="flex flex-row gap-2">
            {persona.public_persona && (
              <Tooltip>
                <TooltipTrigger>
                  <Crown className="w-4 h-4 text-accent" />
                </TooltipTrigger>
                <TooltipContent>Public Agent</TooltipContent>
              </Tooltip>
            )}
            <span className="max-w-52 truncate">{persona.name}</span>
            {persona.web3?.metadata?.symbol ? (
              <div className="text-lg font-normal text-muted-foreground">
                {persona.web3?.metadata?.symbol}
              </div>
            ) : null}
            {persona?.web3?.contractAddress ? (
              <Button
                className="p-0 h-8 w-8 items-center justify-start text-muted-foreground hover:text-white hover:bg-transparent"
                size="lg"
                variant="ghost"
                onClick={(e) => {
                  // Prevents the user from being redirected to the persona page
                  e.preventDefault();

                  if (!persona.web3?.contractAddress) {
                    return;
                  }

                  navigator.clipboard
                    .writeText(persona.web3.contractAddress)
                    .then(() =>
                      toast({ description: "Address copied to clipboard." }),
                    );
                }}
              >
                <Copy className="w-4 h-4" />
              </Button>
            ) : null}
          </div>
          {persona.created_at ? (
            <div
              onClick={(e) => {
                // Prevents the user from being redirected to the persona page when clicking a social media link
                e.stopPropagation();
              }}
              className="flex flex-row items-center gap-2"
            >
              <span className="text-[#6fff1b] text-lg">
                {convertISODateToRelativeTime(persona.created_at)}
              </span>
              {persona.twitter ? (
                <Link
                  to={`https://x.com/${persona.twitter}`}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  <X height={14} width={14} fill="#ffffff" />
                </Link>
              ) : undefined}
              {persona.web3?.contractAddress ? (
                <Link
                  to={`https://solscan.io/token/${persona.web3.contractAddress}`}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  <Solscan height={16} width={16} />
                </Link>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </TableCell>
  );
}
